import { h } from 'vue';
import { createToastInterface, POSITION, TYPE } from 'vue-toastification';
import { BootstrapVariant } from '@/tsEnums/BootstrapVariant';

/**
 * A standalone toast interface, independent of any component instance.
 *
 * Unlike the bootstrap-vue-next version, `createToastInterface()` does not
 * rely on Vue's provide/inject system - there is no "must be called inside
 * setup(), underneath some provider component" restriction to work around.
 * It's safe to construct here, at module scope, and call from anywhere:
 * components, Pinia/Vuex actions, plain .ts utilities, interceptors, etc.
 */
const toast = createToastInterface({
  position: POSITION.TOP_RIGHT,
});

// Maps our existing Bootstrap color variants onto vue-toastification's four
// built-in toast types. Variants without a natural match (e.g. 'light',
// 'secondary', 'dark') fall back to the neutral 'default' type.
const variantToType: Partial<Record<BootstrapVariant, TYPE>> = {
  [BootstrapVariant.success]: TYPE.SUCCESS,
  [BootstrapVariant.danger]: TYPE.ERROR,
  [BootstrapVariant.warning]: TYPE.WARNING,
  [BootstrapVariant.info]: TYPE.INFO,
};

/**
 * Display a toast message with a given title and message. Variant defines which
 * color/icon variant to use (e.g. success, info, warning, ...), and delay defines
 * how long the toast should stay visible before auto-hiding.
 * @param title
 * @param text
 * @param delay in ms
 * @param variant
 */
export const makeToast = (
  title: string,
  text: string,
  delay: number,
  variant: BootstrapVariant
): void => {
  const type: TYPE = variantToType[variant] ?? TYPE.DEFAULT;

  // vue-toastification's content can be a string or a VNode/component - a small
  // VNode here reproduces the title+body layout $bvToast used to give us.
  const content = title ? h('div', [h('strong', title), h('br'), text]) : text;

  toast(content, {
    type,
    timeout: delay,
  });
};

export default { makeToast };
